import React, { useEffect, useRef, useState } from "react";
import apiClient from "../api/client";
import { itemsToPayload } from "./BasketSecuritySection";

const STATUS = {
  SAFE:             { label: "תקין",              color: "var(--success)" },
  WARNING:          { label: "מתקרב לסף אזהרה",   color: "var(--warning, #f59e0b)" },
  BREACH:           { label: "מעל סף הפרה",       color: "var(--danger)" },
  RELEASE_ELIGIBLE: { label: "ניתן לשחרר בטוחות", color: "var(--accent)" },
  NO_PRICE:         { label: "אין שער למניה",     color: "var(--text-secondary)" },
  NO_COLLATERAL:    { label: "ללא בטוחות",        color: "var(--text-secondary)" },
};

const ils = (n) =>
  n == null ? "—" : `₪${Math.round(n).toLocaleString("he-IL")}`;

/**
 * Live LTV for a loan that has not been saved yet.
 *
 * The figure comes from POST /baskets/preview-ltv rather than being recomputed
 * here: the LTV formula handles FX, accrued interest, price formulas and shared
 * baskets, and a second copy in the browser would drift from the number the loan
 * actually gets on save.
 */
export default function LtvPreview({ loan, security }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const seq = useRef(0);

  const { pledgeItems, guaranteeItems } = itemsToPayload(security.collateralItems || []);
  const joiningBasket = security.basketMode === "EXISTING" ? security.basketId : null;

  // Re-price on any input that moves the number. Serialised so the debounce key is
  // the request body itself — no request goes out for a keystroke that changes nothing.
  const body = JSON.stringify({
    basketId: joiningBasket,
    principal: Number(loan.principal) || 0,
    currency: loan.currency || "ILS",
    cashDepositAmount: Number(loan.cashDepositAmount) || 0,
    ltvWarningThreshold: Number(loan.ltvWarningThreshold) || undefined,
    ltvBreachThreshold: Number(loan.ltvBreachThreshold) || undefined,
    ltvPriceFormula: loan.ltvPriceFormula || undefined,
    pledgeItems,
    guaranteeItems,
  });

  useEffect(() => {
    const parsed = JSON.parse(body);
    const nothingToPrice =
      !parsed.principal && parsed.pledgeItems.length === 0 && parsed.guaranteeItems.length === 0;
    if (nothingToPrice) {
      setResult(null);
      return;
    }

    const mine = ++seq.current;
    setLoading(true);
    const timer = setTimeout(() => {
      apiClient.post("/baskets/preview-ltv", parsed)
        .then((res) => {
          if (mine !== seq.current) return; // a newer keystroke already won
          setResult(res.data);
          setFailed(false);
        })
        .catch(() => { if (mine === seq.current) setFailed(true); })
        .finally(() => { if (mine === seq.current) setLoading(false); });
    }, 400);

    return () => clearTimeout(timer);
  }, [body]);

  if (!result && !loading) return null;

  const status = STATUS[result?.ltvStatus] || STATUS.NO_COLLATERAL;
  const hasNumber = result?.currentLTV != null;

  return (
    <div
      className="field form-full-width"
      style={{
        border: "1px solid var(--border-light)", borderRadius: "var(--radius-sm)",
        padding: "14px 16px", background: "var(--bg-subtle)",
        display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap",
      }}
    >
      <div style={{ minWidth: 120 }}>
        <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>LTV צפוי</div>
        <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.1, color: hasNumber ? status.color : "var(--text-secondary)" }}>
          {loading && !result ? "…" : hasNumber ? `${result.currentLTV.toFixed(1)}%` : "—"}
        </div>
        <div style={{ fontSize: 12, color: status.color }}>{status.label}</div>
      </div>

      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 13 }}>
        <div>
          <div style={{ color: "var(--text-secondary)", fontSize: 12 }}>שווי בטוחות</div>
          <div style={{ fontWeight: 600 }}>{ils(result?.collateralValue)}</div>
        </div>
        <div>
          <div style={{ color: "var(--text-secondary)", fontSize: 12 }}>חוב נטו</div>
          <div style={{ fontWeight: 600 }}>{ils(result?.netDebt)}</div>
        </div>
        {result?.guaranteeTotal > 0 && (
          <div>
            <div style={{ color: "var(--text-secondary)", fontSize: 12 }}>ערבויות</div>
            <div style={{ fontWeight: 600 }}>{ils(result.guaranteeTotal)}</div>
          </div>
        )}
      </div>

      <div style={{ marginInlineStart: "auto", fontSize: 12, color: "var(--text-secondary)", maxWidth: 260 }}>
        {failed
          ? "לא ניתן לחשב כרגע — הערך יחושב מחדש לאחר השמירה."
          : joiningBasket
            ? "מחושב מול כלל ההלוואות והבטוחות במסגרת."
            : result?.ltvStatus === "NO_PRICE"
              ? "אין שער עדכני למניה, לכן אין שווי בטוחה."
              : "תצוגה מקדימה — מתעדכן בזמן אמת ומחושב מחדש בשמירה."}
      </div>
    </div>
  );
}
